import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { type Practice } from "@/lib/types";

const PRACTICE_ID_RE = /^[a-z0-9-]+$/;

export const Practices = () => {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["practices"], queryFn: api.listPractices });
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl tracking-tight text-[#1a2b6b]">Ordinace</h2>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="rounded bg-[#1a2b6b] px-4 py-2 text-sm font-medium text-white hover:bg-[#0f1d52]"
        >
          {showForm ? "Zrušit" : "Nová ordinace"}
        </button>
      </div>

      {showForm && (
        <CreatePracticeForm
          onCreated={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ["practices"] });
          }}
        />
      )}

      {query.isPending && <p className="text-gray-500">Načítám…</p>}
      {query.isError && <p className="text-red-600">Chyba: {(query.error as Error).message}</p>}
      {query.isSuccess && query.data.length === 0 && (
        <p className="text-gray-500">
          Zatím žádné ordinace. Klikněte na <span className="font-medium">Nová ordinace</span> pro vytvoření.
        </p>
      )}
      {query.isSuccess && query.data.length > 0 && <PracticeTable practices={query.data} />}
    </div>
  );
};

const PracticeTable = ({ practices }: { practices: Practice[] }) => (
  <div className="overflow-hidden rounded border border-gray-200 bg-white">
    <table className="w-full text-sm">
      <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
        <tr>
          <th className="px-4 py-3 font-medium">ID</th>
          <th className="px-4 py-3 font-medium">Název</th>
          <th className="px-4 py-3 font-medium">Aktivní</th>
          <th className="px-4 py-3 font-medium">Aktualizováno</th>
          <th className="px-4 py-3" />
        </tr>
      </thead>
      <tbody>
        {practices.map((p) => (
          <PracticeRow key={p.id} practice={p} />
        ))}
      </tbody>
    </table>
  </div>
);

const PracticeRow = ({ practice }: { practice: Practice }) => {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.togglePractice(practice.id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["practices"] }),
  });

  return (
    <tr className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
      <td className="px-4 py-3 font-mono text-xs text-gray-700">{practice.id}</td>
      <td className="px-4 py-3">{practice.displayName}</td>
      <td className="px-4 py-3">
        <button
          onClick={() => toggle.mutate(!practice.enabled)}
          disabled={toggle.isPending}
          title={practice.enabled ? "Kliknutím deaktivovat" : "Kliknutím aktivovat"}
          className={
            practice.enabled
              ? "inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 hover:bg-green-100 disabled:opacity-50"
              : "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-200 disabled:opacity-50"
          }
        >
          {practice.enabled ? "Zapnuto" : "Vypnuto"}
        </button>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(practice.updatedAt)}</td>
      <td className="px-4 py-3 text-right">
        <Link
          to={`/practices/${practice.id}`}
          className="text-sm text-[#1a2b6b] hover:underline"
        >
          Upravit →
        </Link>
      </td>
    </tr>
  );
};

const DEFAULT_VOICE_ID = "FnOSwrephKr56xg22eJy"; // matches the DB default; user can change later in PracticeSettings

const CreatePracticeForm = ({ onCreated }: { onCreated: () => void }) => {
  const [id, setId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [enabled, setEnabled] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      api.createPractice({
        id,
        displayName,
        enabled,
        voiceId: DEFAULT_VOICE_ID,
        voiceSpeed: 1.0,
      }),
    onSuccess: () => {
      setId("");
      setDisplayName("");
      setEnabled(true);
      onCreated();
    },
  });

  const idValid = PRACTICE_ID_RE.test(id);
  const canSubmit = idValid && displayName.trim().length > 0 && !mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      className="mb-6 rounded border border-gray-200 bg-white p-6"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600">
            ID (slug)
          </span>
          <input
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="demo-ordinace"
            className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-[#1a2b6b] focus:outline-none"
          />
          {id.length > 0 && !idValid && (
            <span className="mt-1 block text-xs text-red-600">
              Pouze malá písmena, číslice a pomlčky.
            </span>
          )}
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600">
            Název
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="MUDr. Příklad"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
          />
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Aktivní</span>
        </label>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded bg-[#1a2b6b] px-4 py-2 text-sm font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {mutation.isPending ? "Vytvářím…" : "Vytvořit"}
        </button>
        {mutation.isError && (
          <span className="text-sm text-red-600">{(mutation.error as Error).message}</span>
        )}
      </div>
    </form>
  );
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};
